/**
 * Plan of Record Page Settings Service
 *
 * Workspace defaults + per-record layouts for the PoR instrument.
 * Custom pages will extend POR_PAGE_REGISTRY in a future release.
 */

import { getSupabaseClient } from './supabaseClient';
import {
  countVisiblePorPagesFromMap,
  defaultPorLandingSegmentFromMap,
  resolveEffectivePorPageVisibility,
  toApiPageLayout,
  visiblePorSectionOrderFromMap,
  type PorLayoutSource,
} from './porPageLayoutResolver';
import {
  POR_SECTION_LABELS,
  POR_SECTION_ORDER,
  POR_SECTION_PROMPTS,
  type PorSectionType,
} from '../types/plan-of-record';

const WORKSPACE_TABLE = 'workspace_por_page_settings';
const RECORD_TABLE = 'por_record_page_settings';

/** Fixed instrument pages (not narrative sections). */
export type PorFixedPageKey = 'dashboard' | 'priorities' | 'change_log';

export type PorPageKey = PorFixedPageKey | PorSectionType;

export type PorPageGroup = 'overview' | 'record' | 'audit';

export interface PorPageDefinition {
  key: PorPageKey;
  name: string;
  description: string;
  group: PorPageGroup;
  /** Route segment after /plan-of-record/:id/ */
  hrefSegment: string;
}

const FIXED_PAGES: PorPageDefinition[] = [
  {
    key: 'dashboard',
    name: 'Executive Dashboard',
    description: 'KPIs, commitments, signals, risks, executive asks, and proof points at a glance.',
    group: 'overview',
    hrefSegment: 'dashboard',
  },
  {
    key: 'priorities',
    name: 'Strategic Priorities',
    description: 'Ranked initiatives and next-right commitments tied to this Plan of Record.',
    group: 'overview',
    hrefSegment: 'priorities',
  },
];

const SECTION_PAGES: PorPageDefinition[] = POR_SECTION_ORDER.map((type) => ({
  key: type,
  name: POR_SECTION_LABELS[type],
  description: POR_SECTION_PROMPTS[type],
  group: 'record' as const,
  hrefSegment: `section/${type}`,
}));

const AUDIT_PAGES: PorPageDefinition[] = [
  {
    key: 'change_log',
    name: 'Change Log',
    description: 'Append-only governance and condition-change history for this Plan of Record.',
    group: 'audit',
    hrefSegment: 'change-log',
  },
];

/** Single source of truth for built-in PoR pages (custom pages will append here later). */
export const POR_PAGE_REGISTRY: PorPageDefinition[] = [
  ...FIXED_PAGES,
  ...SECTION_PAGES,
  ...AUDIT_PAGES,
];

export const POR_PAGE_GROUP_ORDER: { group: PorPageGroup; label: string }[] = [
  { group: 'overview', label: 'Overview' },
  { group: 'record', label: 'The record' },
  { group: 'audit', label: 'Audit' },
];

export function porPageHrefSegment(key: PorPageKey): string {
  return POR_PAGE_REGISTRY.find((p) => p.key === key)?.hrefSegment ?? 'dashboard';
}

export interface WorkspacePorPageSetting {
  id: string;
  workspaceId: string;
  pageKey: PorPageKey;
  isVisible: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PorRecordPageSetting {
  id: string;
  porId: string;
  pageKey: PorPageKey;
  isVisible: boolean;
  createdAt: string;
  updatedAt: string;
}

function rowToWorkspaceSetting(row: Record<string, unknown>): WorkspacePorPageSetting {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    pageKey: row.page_key as PorPageKey,
    isVisible: row.is_visible as boolean,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

function rowToRecordSetting(row: Record<string, unknown>): PorRecordPageSetting {
  return {
    id: row.id as string,
    porId: row.por_id as string,
    pageKey: row.page_key as PorPageKey,
    isVisible: row.is_visible as boolean,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

function toLayoutRows<T extends { pageKey: PorPageKey; isVisible: boolean }>(
  settings: T[],
): { pageKey: PorPageKey; isVisible: boolean }[] {
  return settings.map((s) => ({ pageKey: s.pageKey, isVisible: s.isVisible }));
}

export async function listPorPageSettings(
  workspaceId: string,
): Promise<{ data: WorkspacePorPageSetting[]; error: string | null }> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from(WORKSPACE_TABLE)
    .select('id, workspace_id, page_key, is_visible, created_at, updated_at')
    .eq('workspace_id', workspaceId)
    .order('page_key');

  if (error) return { data: [], error: error.message };

  if (!data || data.length === 0) {
    const { error: seedError } = await supabase.rpc('seed_workspace_por_page_settings', {
      p_workspace_id: workspaceId,
    });
    if (seedError) return { data: [], error: seedError.message };

    const { data: seeded, error: refetchError } = await supabase
      .from(WORKSPACE_TABLE)
      .select('id, workspace_id, page_key, is_visible, created_at, updated_at')
      .eq('workspace_id', workspaceId)
      .order('page_key');

    if (refetchError) return { data: [], error: refetchError.message };
    return { data: (seeded as Record<string, unknown>[]).map(rowToWorkspaceSetting), error: null };
  }

  return { data: (data as Record<string, unknown>[]).map(rowToWorkspaceSetting), error: null };
}

export async function listPorRecordPageSettings(
  porId: string,
): Promise<{ data: PorRecordPageSetting[]; error: string | null }> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from(RECORD_TABLE)
    .select('id, por_id, page_key, is_visible, created_at, updated_at')
    .eq('por_id', porId)
    .order('page_key');

  if (error) return { data: [], error: error.message };

  if (!data || data.length === 0) {
    const { error: seedError } = await supabase.rpc('seed_por_record_page_settings', {
      p_por_id: porId,
    });
    if (seedError) return { data: [], error: seedError.message };

    const { data: seeded, error: refetchError } = await supabase
      .from(RECORD_TABLE)
      .select('id, por_id, page_key, is_visible, created_at, updated_at')
      .eq('por_id', porId)
      .order('page_key');

    if (refetchError) return { data: [], error: refetchError.message };
    return { data: (seeded as Record<string, unknown>[]).map(rowToRecordSetting), error: null };
  }

  return { data: (data as Record<string, unknown>[]).map(rowToRecordSetting), error: null };
}

export async function setPorPageVisible(
  workspaceId: string,
  pageKey: PorPageKey,
  isVisible: boolean,
): Promise<{ error: string | null }> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.from(WORKSPACE_TABLE).upsert(
    {
      workspace_id: workspaceId,
      page_key: pageKey,
      is_visible: isVisible,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'workspace_id,page_key' },
  );
  return { error: error?.message ?? null };
}

export async function setPorRecordPageVisible(
  porId: string,
  pageKey: PorPageKey,
  isVisible: boolean,
): Promise<{ error: string | null }> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.from(RECORD_TABLE).upsert(
    {
      por_id: porId,
      page_key: pageKey,
      is_visible: isVisible,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'por_id,page_key' },
  );
  return { error: error?.message ?? null };
}

export async function replacePorRecordPageLayout(
  porId: string,
  pages: { pageKey: PorPageKey; isVisible: boolean }[],
): Promise<{ error: string | null }> {
  const visibleCount = pages.filter((p) => p.isVisible).length;
  if (visibleCount < 1) {
    return { error: 'At least one page must remain visible.' };
  }

  await listPorRecordPageSettings(porId);

  for (const page of pages) {
    const { error } = await setPorRecordPageVisible(porId, page.pageKey, page.isVisible);
    if (error) return { error };
  }
  return { error: null };
}

export async function replaceWorkspacePorPageLayout(
  workspaceId: string,
  pages: { pageKey: PorPageKey; isVisible: boolean }[],
): Promise<{ error: string | null }> {
  const visibleCount = pages.filter((p) => p.isVisible).length;
  if (visibleCount < 1) {
    return { error: 'At least one page must remain visible.' };
  }

  await listPorPageSettings(workspaceId);
  for (const page of pages) {
    const { error } = await setPorPageVisible(workspaceId, page.pageKey, page.isVisible);
    if (error) return { error };
  }
  return { error: null };
}

export function resolvePorPageVisibility(
  settings: WorkspacePorPageSetting[],
): Map<PorPageKey, boolean> {
  return resolveEffectivePorPageVisibility(toLayoutRows(settings), []).visibility;
}

export function resolvePorRecordPageVisibility(
  workspaceSettings: WorkspacePorPageSetting[],
  recordSettings: PorRecordPageSetting[],
): { visibility: Map<PorPageKey, boolean>; source: PorLayoutSource } {
  return resolveEffectivePorPageVisibility(
    toLayoutRows(workspaceSettings),
    toLayoutRows(recordSettings),
  );
}

export async function getPorRecordPageLayout(porId: string, workspaceId: string) {
  const [workspaceRes, recordRes] = await Promise.all([
    listPorPageSettings(workspaceId),
    listPorRecordPageSettings(porId),
  ]);
  if (workspaceRes.error) return { data: null, error: workspaceRes.error };
  if (recordRes.error) return { data: null, error: recordRes.error };

  const { visibility, source } = resolvePorRecordPageVisibility(
    workspaceRes.data,
    recordRes.data,
  );
  return { data: toApiPageLayout(visibility, source), error: null };
}

export async function getWorkspacePorPageLayout(workspaceId: string) {
  const { data, error } = await listPorPageSettings(workspaceId);
  if (error) return { data: null, error };
  const { visibility, source } = resolveEffectivePorPageVisibility(toLayoutRows(data), []);
  return { data: toApiPageLayout(visibility, source), error: null };
}

export function visiblePorSectionOrder(visibility: Map<PorPageKey, boolean>): PorSectionType[] {
  return visiblePorSectionOrderFromMap(visibility);
}

export function defaultPorLandingSegment(visibility: Map<PorPageKey, boolean>): string {
  return defaultPorLandingSegmentFromMap(visibility);
}

export function countVisiblePorPages(visibility: Map<PorPageKey, boolean>): number {
  return countVisiblePorPagesFromMap(visibility);
}

export function isValidPorPageKey(key: string): key is PorPageKey {
  return POR_PAGE_REGISTRY.some((p) => p.key === key);
}

/** Copy workspace default visibility onto a specific PoR record. */
export async function resetPorRecordPageLayoutToWorkspace(
  porId: string,
  workspaceId: string,
): Promise<{ error: string | null }> {
  const { data: workspaceRows, error: wsError } = await listPorPageSettings(workspaceId);
  if (wsError) return { error: wsError };

  const { visibility } = resolveEffectivePorPageVisibility(toLayoutRows(workspaceRows), []);

  for (const page of POR_PAGE_REGISTRY) {
    const { error } = await setPorRecordPageVisible(
      porId,
      page.key,
      visibility.get(page.key) !== false,
    );
    if (error) return { error };
  }
  return { error: null };
}
